import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, Input, input, OnInit, output, signal } from "@angular/core";
import { AccountMarketplace, AccountType, Marketplace } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import {
  IButtonDirective,
  ICheckboxComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IIconComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { FormsModule } from "@angular/forms";

export interface AccountSelectionList {
  accountId: string;
  accountName: string;
  marketplaces: MarketplaceSelection[];
  isSeller: boolean;
  amazonToken: string;
}

interface MarketplaceSelection extends AccountMarketplace {
  isSelected: boolean;
  disableReason?: string;
}

@Component({
  selector: "app-select-accounts-page",
  imports: [
    CommonModule,
    IButtonDirective,
    TranslocoDirective,
    ICheckboxComponent,
    ITooltipDirective,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    IIconComponent,
  ],
  templateUrl: "./select-accounts-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAccountsPageComponent implements OnInit {
  protected readonly Marketplaces = Marketplaces;

  cancellation = output<void>();
  selectionChange = output<AccountMarketplace[]>();

  orgId = input.required<number>();

  @Input() preselectedAccountsMarketplaces: AccountMarketplace[] | undefined = undefined;

  @Input() set accountMarketplaces(value: AccountMarketplace[]) {
    this.accountSelectionList.set(this.toAccountSelectionList(value, this.preselectedAccountsMarketplaces));
  }

  @Input() set disabledAccountsMarketplaces(value: Map<string, string | undefined>) {
    this.accountSelectionList.update((lists) =>
      lists.map((list) => ({
        ...list,
        marketplaces: list.marketplaces.map((m) => ({
          ...m,
          disableReason: value.get(list.accountId + "." + m.marketplace),
          isSelected: value.get(list.accountId + "." + m.marketplace) ? false : m.isSelected,
        })),
      })),
    );
  }

  searchValue = signal<string>("");
  loading = input<boolean>(false);

  accountSelectionList = signal<AccountSelectionList[]>([]);

  filteredAccountSelectionList = computed(() =>
    this.accountSelectionList().filter((list) =>
      list.accountName.toLowerCase().includes(this.searchValue().toLowerCase()),
    ),
  );

  isAllSelected = computed(() =>
    this.accountSelectionList().every(
      (list) =>
        this.selectedMarketplaceNumberByAccountId().get(list.accountId) ===
        this.eligibleMarketplacesByAccountId().get(list.accountId)?.length,
    ),
  );
  selectedMarketplaceNumberByAccountId = computed<Map<string, number>>(() =>
    this.accountSelectionList().reduce((acc, list) => {
      acc.set(list.accountId, list.marketplaces.filter((m) => m.isSelected).length);
      return acc;
    }, new Map<string, number>()),
  );
  eligibleMarketplacesByAccountId = computed(() =>
    this.accountSelectionList().reduce((acc, list) => {
      acc.set(
        list.accountId,
        list.marketplaces.filter((m) => !m.disableReason).map((m) => m.marketplace),
      );
      return acc;
    }, new Map<string, Marketplace[]>()),
  );

  ngOnInit() {
    this.emitSelectionChange();
  }

  toggleMarketplaceSelection(accountId: string, marketplace: Marketplace, select: boolean, emit = true) {
    const marketplaceSelection = this.accountSelectionList()
      .find((list) => list.accountId === accountId)
      ?.marketplaces.find((m) => m.marketplace === marketplace);

    if (marketplaceSelection?.disableReason) {
      return;
    }
    this.accountSelectionList.update((lists) => {
      return lists.map((list) => {
        if (list.accountId === accountId) {
          const updatedMarketplaces = list.marketplaces.map((m) => ({
            ...m,
            isSelected: m.marketplace === marketplace ? select : m.isSelected,
          }));
          return {
            ...list,
            marketplaces: updatedMarketplaces,
          };
        }
        return list;
      });
    });
    if (emit) {
      this.emitSelectionChange();
    }
  }

  toggleSelectAllMarketplaces(accountId: string, select: boolean, emit = true) {
    for (const a of this.accountSelectionList().filter((a) => a.accountId === accountId)) {
      for (const m of a.marketplaces) {
        this.toggleMarketplaceSelection(a.accountId, m.marketplace, select, emit);
      }
    }
    if (emit) {
      this.emitSelectionChange();
    }
  }

  toggleSelectAllAccounts() {
    const shouldSelect = !this.isAllSelected();
    for (const a of this.accountSelectionList()) {
      this.toggleSelectAllMarketplaces(a.accountId, shouldSelect, false);
    }
    this.emitSelectionChange();
  }

  private toAccountSelectionList(
    accountMarketplaces: AccountMarketplace[],
    preselectedAccountsMarketplaces: AccountMarketplace[] | undefined,
  ): AccountSelectionList[] {
    const isSelected = preselectedAccountsMarketplaces === undefined;
    const preselectMap = (preselectedAccountsMarketplaces ?? []).reduce(
      (acc: Map<string, Array<Marketplace>>, current: AccountMarketplace) => {
        const key = current.accountId;
        if (acc.has(key)) {
          acc.get(key)?.push(current.marketplace);
        } else {
          acc.set(key, [current.marketplace]);
        }
        return acc;
      },
      new Map<string, Array<Marketplace>>(),
    );

    const groupedByAccount = accountMarketplaces.reduce(
      (acc: Map<string, AccountSelectionList>, current: AccountMarketplace) => {
        const key = current.accountId;
        if (acc.has(key)) {
          acc.get(key)?.marketplaces.push({
            ...current,
            isSelected:
              isSelected ||
              (preselectMap.has(key) &&
                preselectMap.get(key)!.some((marketplace) => marketplace === current.marketplace)),
          });
        } else {
          acc.set(key, {
            ...current,
            marketplaces: [
              {
                ...current,
                isSelected:
                  isSelected ||
                  (preselectMap.has(key) &&
                    preselectMap.get(key)!.some((marketplace) => marketplace === current.marketplace)),
              },
            ],
            isSeller: current.accountType === AccountType.SELLER,
            amazonToken: "",
          });
        }
        return acc;
      },
      new Map<string, AccountSelectionList>(),
    );

    return Array.from(groupedByAccount.values());
  }

  private emitSelectionChange() {
    const toEmit = this.accountSelectionList().flatMap((a) =>
      a.marketplaces
        .filter((m) => m.isSelected)
        .map((m) => ({ ...m, accountId: a.accountId, resourceOrganizationId: this.orgId() })),
    );
    this.selectionChange.emit(toEmit);
  }
}
